import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../Components/Pagination";
import "./CustomerList.css";

const CustomerOrderHistory = () => {
  const [customer, setCustomer] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const { id } = useParams();
  const navigate = useNavigate();

  // Sample customer data
  const sampleCustomers = [
    {
      id: 1,
      name: "SSS Enterprises",
      mobile: "9876543210",
      gst_number: "33AALF51701P1Z1",
      state: "Tamil Nadu",
      city: "Sivakasi",
      pin_code: "626189",
      address: "264/SA THAMMANACKENPATTI SIVAKASI-626189",
      totalOrders: 15,
      totalAmount: 254800.50,
      lastOrderDate: "2025-11-21"
    }
  ];

  // Sample invoices data matching your ViewInvoice structure
  const sampleInvoices = [
    {
      id: 1,
      order_id: "INV-001",
      order_date: "2025-11-21",
      status: "Paid",
      total: 15505.20,
      items: [
        {
          product_name: "Pin Box - (00:4)",
          qty: 73,
          unit: "pcs",
          amount_per_product: 180.00,
          total_per_product: 13140.00
        }
      ],
      final_billing: [
        {
          subtotal: 13140.00,
          gst_total: 2365.20,
          total: 15505.20
        }
      ],
      buyer_detail: {
        name: "SSS Enterprises",
        address: "264/SA THAMMANACKENPATTI SIVAKASI-626189",
        state: "Tamil Nadu",
        tax_number: "33AALF51701P1Z1"
      }
    },
    {
      id: 2,
      order_id: "INV-002",
      order_date: "2025-11-18",
      status: "Pending",
      total: 8850.00,
      items: [
        {
          product_name: "Gum Tape - 3 Inches",
          qty: 50,
          unit: "pcs",
          amount_per_product: 150.00,
          total_per_product: 7500.00
        },
        {
          product_name: "Sparkler Box",
          qty: 10,
          unit: "pcs",
          amount_per_product: 135.00,
          total_per_product: 1350.00
        }
      ],
      final_billing: [
        {
          subtotal: 8850.00,
          gst_total: 1593.00,
          total: 10443.00
        }
      ],
      buyer_detail: {
        name: "SSS Enterprises",
        address: "264/SA THAMMANACKENPATTI SIVAKASI-626189",
        state: "Tamil Nadu",
        tax_number: "33AALF51701P1Z1"
      }
    }
  ];

  useEffect(() => {
    const foundCustomer = sampleCustomers.find(c => c.id === parseInt(id));
    setCustomer(foundCustomer);

    if (foundCustomer) {
      const customerInvoices = sampleInvoices.filter(invoice => 
        invoice.buyer_detail.name === foundCustomer.name
      );
      setInvoices(customerInvoices);
      setFilteredInvoices(customerInvoices);
    }
  }, [id]);

  useEffect(() => {
    let filtered = invoices;

    if (searchTerm) {
      filtered = filtered.filter(invoice =>
        invoice.order_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.items.some(item => 
          item.product_name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter(invoice => invoice.status === statusFilter);
    }

    if (dateFilter) {
      filtered = filtered.filter(invoice => invoice.order_date === dateFilter);
    }

    setFilteredInvoices(filtered);
  }, [searchTerm, statusFilter, dateFilter, invoices]);

  const getStatusVariant = (status) => {
    switch(status) {
      case 'Paid': return 'status-approved';
      case 'Pending': return 'status-pending';
      case 'Overdue': return 'status-rejected';
      case 'Draft': return 'status-default';
      default: return 'status-default';
    }
  };

  const handlePrintInvoice = (invoiceId) => {
    window.open(`/sds_polytechnic/invoiceprint/${invoiceId}`, '_blank');
  };

  const calculateTotalItems = (items) => {
    return items.reduce((total, item) => total + item.qty, 0);
  };

  const calculateTotalAmount = () => {
    return filteredInvoices.reduce((total, invoice) => total + invoice.total, 0);
  };

  const {
    currentPage,
    totalPages,
    currentItems: currentInvoices,
    handlePageChange,
    indexOfFirstItem
  } = usePagination(filteredInvoices, 10);

  if (!customer) {
    return (
      <div className="outer-container">
        <div className="text-center py-5">
          <p>Customer not found.</p>
          <button className="btn-top" onClick={() => navigate(-1)}>
            ← Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="outer-container">
      {/* Header Section */}
      <div className="customer-header">
        <div>
          <h3>Order History</h3>
          <p style={{color: '#6c757d', margin: 0}}>Customer: {customer.name}</p>
        </div>
        <button className="btn-top" onClick={() => navigate(-1)}>
          ← Back to Customers
        </button>
      </div>

      {/* Customer Summary Cards */}
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px'}}>
        <div className="stat-card" style={{backgroundColor: '#294664', color: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center'}}>
          <h4 style={{margin: '0 0 5px 0', fontSize: '1.5rem'}}>{invoices.length}</h4>
          <small>Total Orders</small>
        </div>
        <div className="stat-card" style={{backgroundColor: '#28a745', color: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center'}}>
          <h4 style={{margin: '0 0 5px 0', fontSize: '1.5rem'}}>₹{calculateTotalAmount().toLocaleString('en-IN')}</h4>
          <small>Total Amount</small>
        </div>
        <div className="stat-card" style={{backgroundColor: '#17a2b8', color: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center'}}>
          <h4 style={{margin: '0 0 5px 0', fontSize: '1.5rem'}}>{customer.totalOrders}</h4>
          <small>All Time Orders</small>
        </div>
        <div className="stat-card" style={{backgroundColor: '#ffc107', color: 'black', padding: '15px', borderRadius: '8px', textAlign: 'center'}}>
          <h4 style={{margin: '0 0 5px 0', fontSize: '1.5rem'}}>{new Date(customer.lastOrderDate).toLocaleDateString('en-IN')}</h4>
          <small>Last Order</small>
        </div>
      </div>

      {/* Customer Details */}
      <div style={{backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', marginBottom: '20px', padding: '20px'}}>
        <h5 style={{marginBottom: '15px', color: '#294664'}}>Customer Information</h5>
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px'}}>
          <div>
            <p style={{margin: '5px 0'}}><strong>Name:</strong> {customer.name}</p>
            <p style={{margin: '5px 0'}}><strong>Mobile:</strong> {customer.mobile}</p>
            <p style={{margin: '5px 0'}}><strong>GSTIN:</strong> {customer.gst_number}</p>
          </div>
          <div>
            <p style={{margin: '5px 0'}}><strong>Address:</strong> {customer.address}</p>
            <p style={{margin: '5px 0'}}><strong>City:</strong> {customer.city}, {customer.state} - {customer.pin_code}</p>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <h5 style={{marginBottom: '15px', color: '#294664'}}>Filter Orders</h5>
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px'}}>
          <div>
            <label style={{display: 'block', marginBottom: '5px', fontWeight: '500'}}>Search Orders</label>
            <input
              type="text"
              className="form-control"
              placeholder="Search by invoice no or product..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '4px'}}
            />
          </div>
          <div>
            <label style={{display: 'block', marginBottom: '5px', fontWeight: '500'}}>Status</label>
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '4px'}}
            >
              <option value="all">All Status</option>
              <option value="Paid">Paid</option>
              <option value="Pending">Pending</option>
              <option value="Overdue">Overdue</option>
              <option value="Draft">Draft</option>
            </select>
          </div>
          <div>
            <label style={{display: 'block', marginBottom: '5px', fontWeight: '500'}}>Order Date</label>
            <input
              type="date"
              className="form-control"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              style={{width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '4px'}}
            />
          </div>
          <div>
            <label style={{display: 'block', marginBottom: '5px', fontWeight: '500'}}>&nbsp;</label>
            <button
              className="btn-top"
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("all");
                setDateFilter("");
              }}
              style={{width: '100%'}}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="table-pagination-wrapper">
        <div className="table-pagination-content">
          <table className="customer-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Invoice No</th>
                <th>Order Date</th>
                <th>Status</th>
                <th>Items</th>
                <th>Total Items</th>
                <th className="text-end">Amount (₹)</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentInvoices.map((invoice, index) => (
                <tr key={invoice.id}>
                  <td>{indexOfFirstItem + index + 1}</td>
                  <td>
                    <strong>{invoice.order_id}</strong>
                  </td>
                  <td>{invoice.order_date}</td>
                  <td>
                    <span className={`status-badge ${getStatusVariant(invoice.status)}`}>
                      {invoice.status}
                    </span>
                  </td>
                  <td>
                    <div style={{fontSize: '0.875rem'}}>
                      {invoice.items.slice(0, 2).map((item, i) => (
                        <div key={i}>
                          {item.product_name} ({item.qty} {item.unit})
                        </div>
                      ))}
                      {invoice.items.length > 2 && (
                        <div style={{color: '#6c757d'}}>
                          +{invoice.items.length - 2} more items
                        </div>
                      )}
                    </div>
                  </td>
                  <td>{calculateTotalItems(invoice.items)}</td>
                  <td className="text-end" style={{fontWeight: 'bold'}}>
                    ₹{invoice.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </td>
                  <td>
                    <div className="action-icons">
                      <Link
                        to={`/view-invoice/${invoice.id}`}
                        className="view-icon"
                        title="View Invoice"
                      >
                        👁️
                      </Link>
                      <button
                        className="edit-icon"
                        onClick={() => handlePrintInvoice(invoice.id)}
                        title="Print Invoice"
                      >
                        🖨️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {currentInvoices.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center" style={{padding: '40px'}}>
                    <div style={{color: '#6c757d'}}>
                      <div style={{fontSize: '48px', marginBottom: '10px'}}>📄</div>
                      <p>No orders found for this customer</p>
                      <small>Try adjusting your filters or search terms</small>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

      {/* Summary Footer */}
      {filteredInvoices.length > 0 && (
        <div className="summary-footer">
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px'}}>
            <div>
              <strong>Total Orders:</strong> {filteredInvoices.length}
            </div>
            <div>
              <strong>Total Items:</strong> {filteredInvoices.reduce((total, invoice) => total + calculateTotalItems(invoice.items), 0)}
            </div>
            <div>
              <strong>Total Amount:</strong> ₹{calculateTotalAmount().toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerOrderHistory;